import { useState, useEffect, useCallback } from 'react';
import Button from '../../components/common/Button';
import { CreateTenantModal } from '../../components/modals';
import { tenantsApi } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import './Tenants.css';

// Known tenant API keys (production keys)
const TENANT_KEYS = {
  chat100: {
    publicKey: 'auth1_pk_chat100_3910593125ec8c06e4414f5b31fa762a',
    secretKey: 'auth1_sk_chat100_cf15180ce7d650fa2059711bad7d495d5edebc3e8dd174209bca11dba4fdc206'
  },
  revmine: {
    publicKey: 'auth1_pk_8566b9bfd3cc33eae8f35aff3735b88815168431abb40457',
    secretKey: 'auth1_sk_revmine_5e3685a255da46670b96ff16e1e7435e0c54fd8cd49621426dbb7a857e39f173'
  },
  l100: { publicKey: 'auth1_pk_0d94cdf7830c2e0de02eb3d97d037cab9f940293341c9319' },
  cachee: { publicKey: 'auth1_pk_e9bb16c30f2738fdb1c5e6611a00ec729720e450add64d74' },
  z101: { publicKey: 'auth1_pk_z101_78ee50a0973bc58220c066bbb2d9ee56136bd1edf555cc0c' },
  h33: { publicKey: 'auth1_pk_h33_1f8bcc1bee9f41c809cdf578b8a3fde5' },
  mirror1: { publicKey: 'auth1_pk_m1_fb7012b081832217547dcf76f140909e' },
  babyzilla: { publicKey: 'auth1_pk_bz_d1617d338c62cf8efba4b3d8ae2948c0' },
  babykong: { publicKey: 'auth1_pk_bk_6cff0dc70a7472fb4579eee4add6b685' },
  fundmysba: { publicKey: 'auth1_pk_fmsba_1ec23b24df0d79c00376a2c408885189' },
  sba1: { publicKey: 'auth1_pk_d9bcc00f4923a963281a262680fc115955fb1246b0ce805b' },
  supersearch: { publicKey: 'auth1_pk_search100_b6cfa1289bef3412abd174d8439862a0' },
  case1: { publicKey: 'auth1_pk_case1_99e2d529736d7082cffc395ef0f05226' },
  search100: { publicKey: 'auth1_pk_s100_5d357947b08dfa33fc7cb1e1681b5031' },
  v100: { publicKey: 'auth1_pk_v100_a59d76e484a074ee71b89623f0f32eca' }
};

// Generate Auth1 setup instructions for a tenant
const generateSetupInstructions = (tenant) => {
  const slug = tenant.slug || tenant.name?.toLowerCase().replace(/[^a-z0-9]/g, '') || 'tenant';
  const domain = tenant.domain || `app.${slug}.ai`;
  const keys = TENANT_KEYS[slug] || {};
  const publicKey = tenant.apiKey || keys.publicKey || `auth1_pk_${slug}_[contact_admin]`;
  const secretKey = keys.secretKey || `auth1_sk_${slug}_[contact_admin_for_secret_key]`;

  return `## ${tenant.name} - Auth1 Integration Guide

### Your Auth1 Credentials

| Setting | Value |
|---------|-------|
| **Tenant Slug** | \`${slug}\` |
| **Public Key** | \`${publicKey}\` |
| **Secret Key** | \`${secretKey}\` |
| **Login URL** | \`https://api.z101.ai/login/${slug}\` |
| **Callback URL** | \`https://${domain}/auth/callback\` |

---

### Environment Variables (.env)

\`\`\`env
AUTH1_TENANT_SLUG=${slug}
AUTH1_PUBLIC_KEY=${publicKey}
AUTH1_SECRET_KEY=${secretKey}
AUTH1_API_URL=https://api.auth1.ai
AUTH1_LOGIN_URL=https://api.z101.ai/login/${slug}
AUTH1_CALLBACK_URL=https://${domain}/auth/callback
\`\`\`

---

### Quick Start

#### 1. Add Login Button
Replace your login button with a redirect to Auth1:

\`\`\`javascript
function handleLogin() {
  const callbackUrl = encodeURIComponent('https://${domain}/auth/callback');
  window.location.href = \`https://api.z101.ai/login/${slug}?redirect_uri=\${callbackUrl}\`;
}
\`\`\`

#### 2. Create Callback Handler
Create \`/auth/callback\` route to handle the token:

\`\`\`javascript
// Get token from URL
const params = new URLSearchParams(window.location.search);
const token = params.get('token');

if (token) {
  localStorage.setItem('auth_token', token);
  window.location.href = '/dashboard'; // Redirect to your app
}
\`\`\`

#### 3. Backend Token Validation
\`\`\`javascript
// Validate token with Auth1 (use SECRET key on backend)
const response = await fetch('https://api.auth1.ai/api/auth/session', {
  headers: {
    'Authorization': \`Bearer \${token}\`,
    'X-Api-Key': process.env.AUTH1_SECRET_KEY
  }
});
const session = await response.json();
// session.user contains the authenticated user
\`\`\`

---

### Express Middleware Example

\`\`\`javascript
async function requireAuth(req, res, next) {
  const token = req.headers.authorization?.replace('Bearer ', '');

  if (!token) {
    return res.status(401).json({ error: 'No token provided' });
  }

  try {
    const response = await fetch('https://api.auth1.ai/api/auth/session', {
      headers: {
        'Authorization': \`Bearer \${token}\`,
        'X-Api-Key': process.env.AUTH1_SECRET_KEY
      }
    });

    if (!response.ok) throw new Error('Invalid token');

    const session = await response.json();
    req.user = session.user;
    next();
  } catch (err) {
    return res.status(401).json({ error: 'Invalid token' });
  }
}
\`\`\`

---

### Branding Configuration

Your login page automatically uses your brand settings:
- **Primary Color**: ${tenant.primaryColor || '#10b981'}
- **Theme**: ${tenant.theme || 'dark'}
- **Logo**: Upload via White Label settings

---

### Support
- Auth1 Dashboard: https://auth1-pwa-z101.netlify.app
- Documentation: https://api.auth1.ai/docs
- Support: support@z101.ai
`;
};

const Tenants = () => {
  const { showToast } = useToast();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [setupModal, setSetupModal] = useState({ isOpen: false, tenant: null });
  const [pagination, setPagination] = useState({ page: 1, limit: 12, total: 0 });

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchQuery && { search: searchQuery }),
        ...(statusFilter && { status: statusFilter }),
        ...(planFilter && { plan: planFilter }),
      };

      const response = await tenantsApi.list(params);
      const data = response.companies || response.data || response.items || response || [];

      setTenants(Array.isArray(data) ? data : []);
      setPagination(prev => ({
        ...prev,
        total: response.total || response.pagination?.total || data.length,
      }));
    } catch (err) {
      console.error('Failed to fetch tenants:', err);
      setError(err.message);
      showToast('Failed to load tenants', 'error');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchQuery, statusFilter, planFilter, showToast]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.actions-dropdown')) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (pagination.page !== 1) {
        setPagination(prev => ({ ...prev, page: 1 }));
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleCreateSuccess = (newTenant) => {
    setTenants(prev => [newTenant, ...prev]);
    setIsCreateModalOpen(false);
    showToast('Tenant created successfully', 'success');
  };

  const handleCopySetup = async (tenant) => {
    const instructions = generateSetupInstructions(tenant);
    try {
      await navigator.clipboard.writeText(instructions);
      showToast(`Setup instructions for ${tenant.name} copied to clipboard!`, 'success');
    } catch (err) {
      // Fallback for older browsers
      setSetupModal({ isOpen: true, tenant, instructions });
    }
    setActiveDropdown(null);
  };

  const handleViewSetup = (tenant) => {
    const instructions = generateSetupInstructions(tenant);
    setSetupModal({ isOpen: true, tenant, instructions });
    setActiveDropdown(null);
  };

  const handleSuspend = async (tenantId) => {
    try {
      await tenantsApi.suspend(tenantId);
      setTenants(prev =>
        prev.map(t => (t.id === tenantId ? { ...t, status: 'suspended' } : t))
      );
      showToast('Tenant suspended', 'success');
    } catch (err) {
      showToast('Failed to suspend tenant', 'error');
    }
    setActiveDropdown(null);
  };

  const handleReactivate = async (tenantId) => {
    try {
      await tenantsApi.reactivate(tenantId);
      setTenants(prev =>
        prev.map(t => (t.id === tenantId ? { ...t, status: 'active' } : t))
      );
      showToast('Tenant reactivated', 'success');
    } catch (err) {
      showToast('Failed to reactivate tenant', 'error');
    }
    setActiveDropdown(null);
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toLocaleString();
  };

  const getQuotaPercent = (tenant) => {
    if (tenant.apiUsage && tenant.apiLimit) {
      return Math.min(100, Math.round((tenant.apiUsage / tenant.apiLimit) * 100));
    }
    return tenant.quotaPercent || 0;
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'status-active';
      case 'onboarding': return 'status-onboarding';
      case 'new': return 'status-new';
      case 'suspended': return 'status-suspended';
      default: return 'status-active';
    }
  };

  return (
    <div className="tenants-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">All Tenants</h1>
        </div>
        <div className="page-actions">
          <Button variant="primary" icon="plus" onClick={() => setIsCreateModalOpen(true)}>
            + New Tenant
          </Button>
        </div>
      </div>

      {error && (
        <div className="tenants-error">
          <span className="icon"><svg><use href="#icon-alert-triangle"></use></svg></span>
          <span>{error}</span>
          <Button variant="ghost" size="sm" onClick={fetchTenants}>Retry</Button>
        </div>
      )}

      <div className="tenants-toolbar">
        <div className="filter-group">
          <select
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="onboarding">Onboarding</option>
            <option value="new">New</option>
            <option value="suspended">Suspended</option>
          </select>
          <select
            className="filter-select"
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
          >
            <option value="">All Plans</option>
            <option value="enterprise">Enterprise</option>
            <option value="growth">Growth</option>
            <option value="starter">Starter</option>
          </select>
        </div>
        <div className="search-box">
          <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            placeholder="Search tenants..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Table View */}
      <div className="tenants-table-container">
        <table className="tenants-table">
          <thead>
            <tr>
              <th>NAME</th>
              <th>DOMAIN</th>
              <th>PLAN</th>
              <th>USERS</th>
              <th>QUOTA USAGE</th>
              <th>STATUS</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(6)].map((_, i) => (
                <tr key={i} className="skeleton-row">
                  <td><div className="skeleton-cell"></div></td>
                  <td><div className="skeleton-cell"></div></td>
                  <td><div className="skeleton-cell short"></div></td>
                  <td><div className="skeleton-cell short"></div></td>
                  <td><div className="skeleton-cell"></div></td>
                  <td><div className="skeleton-cell short"></div></td>
                  <td><div className="skeleton-cell short"></div></td>
                </tr>
              ))
            ) : tenants.length > 0 ? (
              tenants.map(tenant => {
                const quotaPercent = getQuotaPercent(tenant);
                const isVerified = tenant.domainVerified !== false;
                return (
                  <tr key={tenant.id}>
                    <td className="tenant-name-cell">
                      <span className="tenant-name">{tenant.name}</span>
                    </td>
                    <td className="tenant-domain-cell">
                      <span className="domain-text">{tenant.domain || tenant.subdomain || '-'}</span>
                      {isVerified ? (
                        <svg className="domain-verified" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                          <polyline points="22 4 12 14.01 9 11.01"></polyline>
                        </svg>
                      ) : (
                        <svg className="domain-pending" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"></path>
                        </svg>
                      )}
                    </td>
                    <td>{tenant.plan || tenant.planName || '-'}</td>
                    <td>{formatNumber(tenant.usersCount || tenant.users_count || tenant.users)}</td>
                    <td>{quotaPercent}%</td>
                    <td>
                      <span className={`status-badge ${getStatusClass(tenant.status)}`}>
                        {(tenant.status || 'active').toUpperCase()}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <div className="actions-dropdown">
                        <button
                          className="actions-trigger"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveDropdown(activeDropdown === tenant.id ? null : tenant.id);
                          }}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="1"></circle>
                            <circle cx="12" cy="5" r="1"></circle>
                            <circle cx="12" cy="19" r="1"></circle>
                          </svg>
                        </button>
                        {activeDropdown === tenant.id && (
                          <div className="dropdown-menu">
                            <button onClick={() => handleViewSetup(tenant)}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                              </svg>
                              View Setup
                            </button>
                            <button onClick={() => handleCopySetup(tenant)}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                              </svg>
                              Copy Setup
                            </button>
                            <button>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                              </svg>
                              Edit Settings
                            </button>
                            <button>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"></circle>
                                <path d="M12 6v6l4 2"></path>
                              </svg>
                              Edit Branding
                            </button>
                            <button>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"></path>
                              </svg>
                              Rotate API Key
                            </button>
                            <div className="dropdown-divider"></div>
                            {tenant.status === 'suspended' ? (
                              <button onClick={() => handleReactivate(tenant.id)} className="action-success">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                </svg>
                                Reactivate
                              </button>
                            ) : (
                              <button onClick={() => handleSuspend(tenant.id)} className="action-danger">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="12" cy="12" r="10"></circle>
                                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
                                </svg>
                                Disable Tenant
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="7" className="empty-state">
                  <div className="empty-content">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M3 21h18M3 10h18M3 7l9-4 9 4M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3"></path>
                    </svg>
                    <h3>No tenants found</h3>
                    <p>Get started by creating your first tenant</p>
                    <Button variant="primary" icon="plus" onClick={() => setIsCreateModalOpen(true)}>
                      + New Tenant
                    </Button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!loading && tenants.length > 0 && pagination.total > pagination.limit && (
        <div className="tenants-pagination">
          <Button
            variant="secondary"
            size="sm"
            disabled={pagination.page <= 1}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
          >
            Previous
          </Button>
          <span className="pagination-info">
            Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
          >
            Next
          </Button>
        </div>
      )}

      {/* Setup Instructions Modal */}
      {setupModal.isOpen && (
        <div className="modal-overlay" onClick={() => setSetupModal({ isOpen: false, tenant: null })}>
          <div className="setup-modal" onClick={e => e.stopPropagation()}>
            <div className="setup-modal-header">
              <h2>Setup Instructions - {setupModal.tenant?.name}</h2>
              <button
                className="modal-close"
                onClick={() => setSetupModal({ isOpen: false, tenant: null })}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="setup-modal-content">
              <pre>{setupModal.instructions}</pre>
            </div>
            <div className="setup-modal-footer">
              <Button
                variant="secondary"
                onClick={() => setSetupModal({ isOpen: false, tenant: null })}
              >
                Close
              </Button>
              <Button
                variant="primary"
                onClick={async () => {
                  await navigator.clipboard.writeText(setupModal.instructions);
                  showToast('Copied to clipboard!', 'success');
                }}
              >
                Copy to Clipboard
              </Button>
            </div>
          </div>
        </div>
      )}

      <CreateTenantModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
};

export default Tenants;
